import React from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { get, del } from '../utils/api';
import PageLayout from '../components/common/PageLayout';

const STATUS_COLOR = { scheduled: 'info', sending: 'warning', sent: 'success', cancelled: 'default', failed: 'error' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDateTime(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }
function dayKey(iso) { try { return new Date(iso).toISOString().slice(0, 10); } catch { return String(iso).slice(0, 10); } }
function fmtDayHeader(key) {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const wd = dt.toLocaleDateString(undefined, { weekday: 'long', timeZone: 'UTC' });
  return `${wd}, ${MONTHS[m - 1]} ${d}, ${y}`;
}

export default function ScheduledEmailsPage() {
  const [schedules, setSchedules] = React.useState([]);
  const [listMeta, setListMeta] = React.useState({});
  const [view, setView] = React.useState('list');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [ok, setOk] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await get('/api/manage/email-schedules');
      const arr = Array.isArray(res?.schedules) ? res.schedules : [];
      setSchedules(arr);
      const ids = [...new Set(arr.flatMap((s) => (Array.isArray(s.list_ids) ? s.list_ids.map(String) : [])))];
      if (ids.length) {
        try {
          const lm = await get(`/api/manage/getresponse-lists?ids=${encodeURIComponent(ids.join(','))}`);
          const map = {};
          (lm?.lists || []).forEach((l) => { map[String(l.id)] = l; });
          setListMeta(map);
        } catch { /* fall back to raw ids */ }
      }
    } catch (e) { setError(e?.message || 'Failed to load scheduled emails'); }
    finally { setLoading(false); }
  }, []);

  const listLabel = React.useCallback((id) => {
    const m = listMeta[String(id)];
    if (!m) return String(id);
    return m.count != null ? `${m.name} (${m.count})` : m.name;
  }, [listMeta]);
  const renderLists = React.useCallback((ids) => (Array.isArray(ids) ? ids : []).map(listLabel).join(', '), [listLabel]);

  React.useEffect(() => { load(); }, [load]);

  const onCancel = async (id) => {
    setError(''); setOk('');
    try { await del(`/api/manage/email-schedule/${id}`); setOk('Schedule cancelled.'); await load(); }
    catch (e) { setError(e?.message || 'Failed to cancel'); }
  };

  const byDay = React.useMemo(() => {
    const map = new Map();
    schedules.forEach((s) => {
      const k = dayKey(s.send_at);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(s);
    });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [schedules]);

  const counts = React.useMemo(() => {
    const c = { scheduled: 0, sent: 0, failed: 0, cancelled: 0, sending: 0 };
    schedules.forEach((s) => { c[s.status] = (c[s.status] || 0) + 1; });
    return c;
  }, [schedules]);

  const row = (s) => (
    <TableRow key={s.id} hover>
      <TableCell>{s.event_title || s.event_key}</TableCell>
      <TableCell>{s.is_send_now ? 'Send now' : `${s.days_before} day(s) before`}</TableCell>
      <TableCell>{fmtDateTime(s.send_at)}</TableCell>
      <TableCell>{renderLists(s.list_ids)}</TableCell>
      <TableCell><Chip size="small" label={s.status} color={STATUS_COLOR[s.status] || 'default'} variant="outlined" /></TableCell>
      <TableCell sx={{ maxWidth: 240, color: 'error.main', fontSize: 12 }}>{s.error_text || (s.sent_at ? `sent ${fmtDateTime(s.sent_at)}` : '')}</TableCell>
      <TableCell align="right">
        {s.status === 'scheduled' ? (
          <Tooltip title="Cancel"><IconButton size="small" color="error" onClick={() => onCancel(s.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
        ) : null}
      </TableCell>
    </TableRow>
  );

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Scheduled Event Emails</Typography>
          <Typography variant="body2" color="text.secondary">
            All calendar-event email schedules. Set them up per event from Manage → Calendar Events → Email.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
            <ToggleButton value="list">List</ToggleButton>
            <ToggleButton value="calendar">Calendar</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh"><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></Tooltip>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        <Chip label={`Scheduled: ${counts.scheduled || 0}`} color="info" variant="outlined" />
        <Chip label={`Sent: ${counts.sent || 0}`} color="success" variant="outlined" />
        <Chip label={`Failed: ${counts.failed || 0}`} color="error" variant="outlined" />
        <Chip label={`Cancelled: ${counts.cancelled || 0}`} variant="outlined" />
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
      {ok ? <Alert severity="success" sx={{ mb: 1.5 }}>{ok}</Alert> : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : schedules.length === 0 ? (
        <Paper sx={{ p: 3 }}><Typography>No scheduled emails yet.</Typography></Paper>
      ) : view === 'list' ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Event</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Send at</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>List (recipients)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Detail</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>{schedules.map(row)}</TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Stack spacing={2}>
          {byDay.map(([key, items]) => (
            <Paper key={key} sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>{fmtDayHeader(key)}</Typography>
              <Stack spacing={0.5}>
                {items.map((s) => (
                  <Stack key={s.id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5, borderBottom: '1px solid #eee' }}>
                    <Chip size="small" label={s.status} color={STATUS_COLOR[s.status] || 'default'} variant="outlined" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{new Date(s.send_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>{s.event_title || s.event_key} {s.is_send_now ? '(send now)' : `(${s.days_before}d before)`}</Typography>
                    {s.status === 'scheduled' ? (
                      <Tooltip title="Cancel"><IconButton size="small" color="error" onClick={() => onCancel(s.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </PageLayout>
  );
}
